export type PushPermission = 'default' | 'granted' | 'denied';

export type PushKeys = { endpoint: string; keys: { p256dh: string; auth: string } };

// Push behind an interface (UI architecture §8 seam 2): the web app uses the browser Push API; a
// native app adds its own transport later.
export interface PushTransport {
  isSupported(): boolean;
  permission(): PushPermission;
  subscribe(vapidPublicKey: string): Promise<PushKeys>;
  unsubscribe(): Promise<boolean>;
  // This device's subscription, read without prompting (UI architecture §8).
  getSubscription(): Promise<PushKeys | null>;
}

// The VAPID public key is base64url (GET /api/me); pushManager.subscribe takes its bytes.
function base64UrlBytes(value: string) {
  const binary = atob(value.replaceAll('-', '+').replaceAll('_', '/'));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

// Only the endpoint and the keys. toJSON() always carries them; the DOM types mark them optional.
function keysOf(subscription: PushSubscription): PushKeys {
  const { endpoint, keys } = subscription.toJSON() as PushKeys;
  return { endpoint, keys };
}

// The browser Push API (UI architecture §12). subscribe() asks for permission only while it is
// still 'default', so call it from a tap, never on page load.
export function createWebPushTransport(): PushTransport {
  const isSupported = () =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return {
    isSupported,
    permission: () => Notification.permission,
    async subscribe(vapidPublicKey) {
      const permission =
        Notification.permission === 'default'
          ? await Notification.requestPermission()
          : Notification.permission;
      if (permission !== 'granted') throw new Error('Notification permission was not granted.');
      const { pushManager } = await navigator.serviceWorker.ready;
      const subscription = await pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlBytes(vapidPublicKey),
      });
      return keysOf(subscription);
    },
    async unsubscribe() {
      const { pushManager } = await navigator.serviceWorker.ready;
      const subscription = await pushManager.getSubscription();
      return subscription === null ? false : subscription.unsubscribe();
    },
    // Reads the subscription the browser already holds: never prompts, never subscribes.
    // getRegistration(), not ready: ready never settles while no service worker is registered.
    async getSubscription() {
      if (!isSupported()) return null;
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration === undefined) return null;
      const subscription = await registration.pushManager.getSubscription();
      return subscription === null ? null : keysOf(subscription);
    },
  };
}
